import { Skeleton } from '@/components/ui/skeleton';

type SkeletonLineProps = {
  width?: string;
  height?: string;
  className?: string;
};

type SkeletonBlockProps = {
  width?: string;
  height?: string;
  className?: string;
};

type TableSkeletonProps = {
  rows?: number;
  columns?: number;
};

type CardSkeletonProps = {
  lines?: number;
};

type ChartSkeletonProps = {
  height?: string;
};

export function SkeletonLine({
  width = '100%',
  height = '1rem',
  className = '',
}: SkeletonLineProps) {
  return <Skeleton className={className} style={{ width, height }} />;
}

export function SkeletonBlock({
  width = '100%',
  height = '4rem',
  className = '',
}: SkeletonBlockProps) {
  return <Skeleton className={`rounded-xl ${className}`} style={{ width, height }} />;
}

export function TableSkeleton({ rows = 5, columns = 3 }: TableSkeletonProps) {
  const headerColumns = Array.from({ length: columns }, (_, columnIndex) => ({
    id: `header-${columnIndex + 1}`,
    width: columnIndex === 0 ? '40%' : '20%',
  }));
  const rowConfigs = Array.from({ length: rows }, (_, rowIndex) => ({
    id: `row-${rowIndex + 1}`,
    cells: Array.from({ length: columns }, (_, columnIndex) => ({
      id: `row-${rowIndex + 1}-cell-${columnIndex + 1}`,
      width: columnIndex === 0 ? '50%' : '20%',
    })),
  }));

  return (
    <div className="w-full space-y-3">
      <div className="flex gap-4">
        {headerColumns.map((column) => (
          <SkeletonLine key={column.id} height="0.75rem" width={column.width} />
        ))}
      </div>
      {rowConfigs.map((row) => (
        <div key={row.id} className="flex gap-4 py-2">
          {row.cells.map((cell) => (
            <SkeletonLine key={cell.id} height="1rem" width={cell.width} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function CardSkeleton({ lines = 3 }: CardSkeletonProps) {
  const lineConfigs = Array.from({ length: lines }, (_, lineIndex) => ({
    id: `line-${lineIndex + 1}`,
    width: lineIndex === lines - 1 ? '40%' : '90%',
  }));

  return (
    <div className="space-y-3 rounded-2xl border border-border bg-card p-4 sm:p-5">
      <SkeletonLine width="60%" height="1.25rem" />
      {lineConfigs.map((line) => (
        <SkeletonLine key={line.id} width={line.width} height="0.875rem" />
      ))}
    </div>
  );
}

export function ChartSkeleton({ height = '16rem' }: ChartSkeletonProps) {
  return <SkeletonBlock width="100%" height={height} />;
}
